import SpeechProviderInterface from '../SpeechProviderInterface';

/**
 * BrowserTranscriberProvider uses the Web Speech API for real-time transcription.
 * It emits 'transcript', 'error', 'start', and 'stop' events.
 */
export default class BrowserTranscriberProvider extends SpeechProviderInterface {
    constructor({ interim = true } = {}) {
        super();

        // Check browser support for SpeechRecognition API
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) throw new Error('Browser does not support SpeechRecognition');

        // Initialize SpeechRecognition instance
        this.recognition = new SpeechRecognition();
        this.recognition.continuous = true; // Keep listening continuously
        this.recognition.interimResults = interim; // Emit partial results if enabled
        this.recognition.lang = 'en-US'; // Default language

        this.isListening = false;
        this.stoppedByUser = false;

        // Handle recognition result
        this.recognition.onresult = (event) => {
            let interimTranscript = '';
            let finalTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i += 1) {
                const result = event.results[i];
                if (result.isFinal) {
                    finalTranscript += result[0].transcript;
                } else {
                    interimTranscript += result[0].transcript;
                }
            }

            if (interimTranscript.trim()) {
                this.emit('transcript-interim', interimTranscript.trim());
                this.emit('listening', true); // actively speaking
            }

            if (finalTranscript.trim()) {
                this.emit('transcript', finalTranscript.trim());
                this.emit('listening', false); // silence detected
            }
        };

        // Handle recognition error
        this.recognition.onerror = (event) => {
            if (this.debug) console.error("[Browser STT] Error:", event.error);

            // Handle mic permission denial
            if (event.error === "not-allowed" || event.error === "service-not-allowed") {
                this.emit("error", "Microphone permission denied");
                this.stop();
            }

            // Handle network or other errors gracefully
            if (event.error === "network" || event.error === "aborted") {
                this.emit("error", "Network or STT aborted");
                this.stop();
            }
        };

        // Handle recognition end (could be natural or due to error/stop)
        this.recognition.onend = () => {
            this.isListening = false;

            if (!this.stoppedByUser) {
                // Auto-restart if not stopped intentionally
                try {
                    this.recognition.start();
                    this.isListening = true;
                } catch (e) {
                    console.warn('Auto-restart failed:', e.message);
                    this.emit('error', e);
                }
            } else {
                this.stoppedByUser = false;
                this.emit('stop'); // Emit stop event when intentionally stopped
            }
        };
    }

    /**
     * Starts the speech recognition if not already started.
     */
    start() {
        if (this.isListening) return;
        try {
            this.recognition.start();
            this.isListening = true;
            this.emit('start'); // Emit start event
        } catch (e) {
            console.warn('Start failed:', e.message);
            this.emit('error', e);
        }
    }

    /**
     * Stops the speech recognition if running.
     */
    stop() {
        if (!this.isListening) return;
        this.stoppedByUser = true;
        this.recognition.stop(); // Will trigger onend
        this.isListening = false;
    }

    /**
     * Returns current status: 'listening' or 'idle'.
     */
    getStatus() {
        return this.isListening ? 'listening' : 'idle';
    }
}
